import React, { useCallback, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Loja from '~/components/Loja';
import {
  BuscaParceiro,
  InputAsyncSelect,
  InputDate,
  InputNumber,
  InputSelect,
} from '~/components/NovosInputs';
import Separator from '~/components/Separator';
import { useEmissaoNFE } from '~/pages/EmissaoNFE/EmissaoNFEContext';
import { ListaDeCFOP } from '~/pages/EmissaoNFE/protocols';
import { tipoLocalConsumo } from '~/pages/EmissaoNFE/utils/tipoLocalConsumo';
import api from '~/services/api';
import { getUserData } from '~/services/user';

const MySwal = withReactContent(Swal);

export const Capa: React.FC = () => {
  const {
    codLoja,
    setCodLoja,
    loja,
    changeLoja,
    setParametrosLoja,
    changeParceiro,
    formNFE: {
      register,
      formState,
      control,
      getValues,
      setValue,
      clearErrors,
      watch,
    },
    setIsBonificado,
    formItens: { setValue: setValueFormItens },
    isUpdate,
    setProdutos,
    produtos,
    setCuponsReferenciados,
    setListaDeCfop,
    formFinanceiro: {
      setValue: setValueFinanceiro,
      getValues: getValuesFinanceiro,
    },
    formDadosEntrega: {
      getValues: getValuesDadosEntrega,
      setError: setErrorDadosEntrega,
    },
    isDisabled,
    setQtdAgendamento,
    setTipoOperacao,
    setCurrentTab,
    localConsumoDesabilitado,
  } = useEmissaoNFE();
  const dataEmissao = watch('dta_emissao');
  const dataSaida = watch('dta_saida');
  const user = getUserData();

  const delay = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  useEffect(() => {
    if (dataEmissao !== '' && dataSaida !== '') {
      /**
       * Ao selecionar as datas o input Finalizadora recebe seu próprio valor, pois o componente de calculo de condicao
       * acompanha em tempo real o valor do componente finalizadora, fazendo assim com que seja forçado o recalculo automático
       * da data de vencimento na aba Financeiro.
       */
      setValueFinanceiro('finalizadora', getValuesFinanceiro('finalizadora'));
    }
  }, [dataEmissao, dataSaida, getValuesFinanceiro, setValueFinanceiro]);

  useEffect(() => {
    (async () => {
      if (user.loja) {
        const { data } = await api.get(`/lojas/${user.loja}`);
        if (data.success) {
          setValue('loja', data.data.cod_loja);
          setCodLoja(data.data.cod_loja);
          changeLoja({
            uf: data.data.des_uf,
            cod_loja: data.data.cod_loja,
            tipo_regime: data.data.tipo_regime,
            cod_cidade: data.data.cod_cidade,
            empresa: {
              flg_envia_ibs_cbs:
                data.data?.regra_empresa?.flg_envia_ibs_cbs || false,
              per_cbs_teste: data.data?.regra_empresa?.per_cbs_teste,
              per_ibs_mun_teste: data.data?.regra_empresa?.per_ibs_mun_teste,
              per_ibs_uf_teste: data.data?.regra_empresa?.per_ibs_uf_teste,
            },
          });
        }
      }
    })();
  }, [user.loja]);

  useEffect(() => {
    setValue('cod_especie', {
      value: 33,
      label: 'NFE - NOTA FISCAL ELETRÔNICA',
      des_doc_fiscal: 'NOTA FISCAL ELETRÔNICA',
      cod_doc_fiscal: '55',
      num_modelo_fiscal: '',
      des_especie: 'NFE',
    });
  }, [setValue]);

  const getParametrosLoja = useCallback(
    async (codigo: number) => {
      const res = await api.get(`regraloja/${codigo}`);
      const { data, success, message } = res.data;

      if (!success) {
        throw new Error(message);
      }

      setParametrosLoja(data);
    },
    [setParametrosLoja],
  );

  const getCfopsByPerfil = useCallback(
    async (codPerfil: number) => {
      /**
       * Efetua busca de CFOPs relacionados
       */
      const { data } = await api.get('/emissao-nfe/cfop', {
        params: {
          cod_perfil_selecionado: codPerfil,
        },
      });
      if (data.success) {
        const options = data.data.map((cfop: ListaDeCFOP) => ({
          value: cfop.cfop,
          label: `${cfop.cfop} - ${cfop.des_cfop}`,
          ...cfop,
        }));
        setListaDeCfop(options);
      }
    },
    [setListaDeCfop],
  );

  const handleChangePerfil = useCallback(
    async (selected: any) => {
      if (
        selected.flg_gera_financeiro &&
        (selected.cod_categoria === null || selected.cod_cc === null)
      ) {
        toast.warn(
          `Dados financeiros do perfil '${selected.cod_perfil} - ${selected.des_perfil}' precisam ser corretamente preenchidos. Vá até ao cadastro de perfil, e complete seu cadastro.`,
        );
        setValue('cod_perfil', '');
        return;
      }
      const perfil = getValues('cod_perfil')?.value;
      if (perfil === undefined || produtos.length <= 0) {
        clearErrors('cod_perfil');
        setValue('cod_perfil', selected);
        setValueFormItens('cfop', '');
        setValueFormItens('bonificado', '');
        setIsBonificado(false);
        getCfopsByPerfil(selected.value);
        return;
      }
      const alteraPerfil = await MySwal.fire({
        title: 'Deseja alterar o perfil selecionado?',
        text: 'Todos os itens da tabela serão removidos.',
        showCancelButton: true,
        confirmButtonColor: '#8850BF',
        cancelButtonColor: '#DE350B',
        confirmButtonText: 'Sim',
        cancelButtonText: 'Não',
      }).then((result) => {
        if (result.isConfirmed) {
          return true;
        }
        return false;
      });
      if (alteraPerfil) {
        clearErrors('cod_perfil');
        setValue('cod_perfil', selected);
        setValueFormItens('cfop', '');
        setValueFormItens('bonificado', '');
        setIsBonificado(false);
        setProdutos([]);
        setCuponsReferenciados([]);
        getCfopsByPerfil(selected.value);
      }
    },
    [
      clearErrors,
      getCfopsByPerfil,
      getValues,
      produtos.length,
      setCuponsReferenciados,
      setIsBonificado,
      setProdutos,
      setValue,
      setValueFormItens,
    ],
  );

  /**
   * Valida os dados de entrega quando o Local de Consumo for "OUTROS".
   * Seta erro somente nos campos que estiverem vazios.
   */
  const handleLocalConsumo = useCallback(() => {
    const localConsumo = getValues('tipo_local_consumo');

    // Se não selecionou ou não é "OUTROS", não faz nada
    if (!localConsumo || Number(localConsumo.value) !== 2) return;

    // Lê os campos necessários nos "dados de entrega"
    const cep = getValuesDadosEntrega('num_cep_entrega');
    const logradouro = getValuesDadosEntrega('des_logradouro_entrega');
    const numero = getValuesDadosEntrega('num_endereco_entrega');
    const bairro = getValuesDadosEntrega('des_bairro_entrega');
    const cidade = getValuesDadosEntrega('cod_cidade_entrega');

    // Lista de campos e regras de "vazio"
    const fields = [
      { name: 'num_cep_entrega', value: cep, isSelect: false },
      { name: 'des_logradouro_entrega', value: logradouro, isSelect: false },
      { name: 'num_endereco_entrega', value: numero, isSelect: false },
      { name: 'des_bairro_entrega', value: bairro, isSelect: false },
      { name: 'cod_cidade_entrega', value: cidade, isSelect: true },
    ] as const;

    const missing: string[] = [];

    fields.forEach(({ name, value, isSelect }) => {
      const isEmpty = isSelect
        ? !value || !value?.value
        : value == null || String(value).trim() === '';

      if (isEmpty) {
        setErrorDadosEntrega(name as any, { type: 'required' });
        missing.push(name);
      }
    });

    if (missing.length > 0) {
      toast.warning(
        'Dados do local de consumo devem ser informados nos dados de entrega.',
        { toastId: 'toast:localConsumoCamposFaltando' },
      );
      setCurrentTab('dados-entrega');
    }
  }, [getValues, getValuesDadosEntrega, setCurrentTab, setErrorDadosEntrega]);

  return (
    <>
      <Separator labelText="Capa da Nota" sidePadding="0 0" />
      <Row>
        <Col md={5} sm={12} style={{ marginTop: '0.5rem' }}>
          <Loja
            selectedLoja={codLoja}
            onChangedObject={(e) => {
              changeLoja(e);
            }}
            onChange={(e) => {
              clearErrors('loja');
              setValue('loja', e);
              getParametrosLoja(Number(e));
              setCodLoja(Number(e));
            }}
            isDisabled={isUpdate}
            disabled={isUpdate}
          />
        </Col>
        <Col md={5} sm={12}>
          <BuscaParceiro
            label="Cliente *"
            placeholder="Selecione..."
            name="busca_parceiro"
            register={register}
            isError={!!formState.errors.busca_parceiro}
            control={control}
            customOptions={{
              buscarPor: { cliente: true },
              fields: ['cod_cliente'],
            }}
            changeSelected={(selected: any) => {
              const {
                des_uf,
                qtd_agendamento_pagar,
                qtd_agendamento_receber,
              }: any = selected;
              if (!des_uf) {
                toast.warning(
                  `Endereço incompleto, atualize o cadastro do parceiro para continuar.`,
                );
              }
              setQtdAgendamento({
                pagar: Number(qtd_agendamento_pagar || 0),
                receber: Number(qtd_agendamento_receber || 0),
              });
              changeParceiro(selected);
              clearErrors('busca_parceiro');
              setValue('busca_parceiro', selected);
            }}
            disabled={isUpdate || produtos.length > 0}
            codLoja={47}
          />
        </Col>
        <Col md={2} sm={12}>
          <InputSelect
            label="Local do Consumo"
            name="tipo_local_consumo"
            placeholder="Selecione..."
            register={register}
            control={control}
            options={tipoLocalConsumo}
            isError={false}
            changeSelected={async (selected) => {
              setValue(
                'tipo_local_consumo',
                [0, 1, 2].includes(Number(selected?.value)) ? selected : null,
              );

              await delay(200);
              handleLocalConsumo();
            }}
            disabled={isUpdate || localConsumoDesabilitado}
          />
        </Col>
        <Col md={4} sm={12}>
          <InputAsyncSelect
            label="Perfil *"
            maxLength={50}
            listWidth="650px"
            placeholder="Selecione..."
            name="cod_perfil"
            register={register}
            isError={!!formState.errors.cod_perfil}
            control={control}
            changeSelected={(selected: any) => {
              handleChangePerfil(selected);
              setTipoOperacao(selected?.tipo_operacao);
              setValueFinanceiro('categoria', '');
              setValueFinanceiro('conta', '');
            }}
            title={
              !getValues('busca_parceiro')?.des_uf || !loja?.cod_loja
                ? 'É necessário selecionar parceiro e loja'
                : ''
            }
            disabled={
              !getValues('busca_parceiro')?.des_uf ||
              !loja?.cod_loja ||
              isUpdate ||
              produtos.length > 0
            }
            api={{
              route: '/perfil-emissao-nf',
              routeParam: loja?.cod_loja,
              bodyParams: {
                flg_interestadual:
                  loja?.uf !== getValues('busca_parceiro')?.des_uf,
              },
              method: 'get',
              fields: ['cod_perfil', 'des_perfil'],
              dependsOf: ['routeParam', 'flg_interestadual'],
              searchBeforeFilter: true,
              messageForDependsOf: 'Selecine o Cliente Para Continuar.',
            }}
          />
        </Col>
        <Col md={1} sm={12}>
          <InputAsyncSelect
            label="Série *"
            maxLength={50}
            placeholder="Série"
            name="cod_serie"
            register={register}
            isError={!!formState.errors.cod_serie}
            control={control}
            disabled={
              !loja?.cod_loja || isUpdate || isDisabled || produtos.length > 0
            }
            title={!loja?.cod_loja ? 'É necessário selecionar uma loja' : ''}
            changeSelected={(selected: any) => {
              clearErrors('cod_serie');
              setValue('cod_serie', selected);
            }}
            api={{
              route: `/serienf/especie/${codLoja}/nfe`,
              method: 'get',
              fields: ['num_serie'],
              searchBeforeFilter: true,
            }}
          />
        </Col>
        <Col md={1} sm={12}>
          <InputSelect
            label="Espécie"
            maxLength={50}
            placeholder="Espécie"
            name="cod_especie"
            register={register}
            isError={!!formState.errors.cod_especie}
            control={control}
            options={[{ value: 33, label: 'NFE - NOTA FISCAL ELETRÔNICA' }]}
            disabled
            changeSelected={(selected) => {
              setValue('cod_especie', selected);
            }}
          />
        </Col>
        <Col md={2} sm={12}>
          <InputNumber
            label="Nº NF"
            max={10000}
            maxLength={50}
            placeholder="0"
            name="num_nf"
            register={register}
            title="Registro gerado automaticamente"
            disabled
            isError={!!formState.errors.num_nf}
          />
        </Col>
        <Col md={2} sm={12}>
          <InputDate
            register={register}
            label="Emissão *"
            name="dta_emissao"
            control={control}
            disabled={isUpdate}
            isError={!!formState.errors.dta_emissao}
            typeError={formState.errors.dta_emissao?.message}
          />
        </Col>
        <Col md={2} sm={12}>
          <InputDate
            label="Saída *"
            register={register}
            name="dta_saida"
            control={control}
            disabled={isUpdate}
            isError={!!formState.errors.dta_saida}
            typeError={formState.errors.dta_saida?.message}
          />
        </Col>
      </Row>
    </>
  );
};
